import React from 'react';
import cookie from 'js-cookie';
import algoliaClient from '../api/algolia';
import {
    createCart,
    fetchCart,
    addToCart,
    updateCartLine,
    removeCartLine,
} from '../api/shopifyStorefront';
import { encode } from 'shopify-gid';

const productSearchIndex = algoliaClient.initIndex(
    process.env.GATSBY_ALGOLIA_PRODUCT_INDEX || 'FENN'
);
productSearchIndex.setSettings({ hitsPerPage: 1000 });

const CART_KEY = 'fennstore_cart_id';
const BANNER_KEY = 'fennstore_banner';
const NEWSLETTER_KEY = 'fennstore_newsletter';

const ShopContext = React.createContext();
export default ShopContext;

export class ShopContextProvider extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isCartOpen: false,
            isAddingToCart: false,
            isSearchOpen: false,
            products: [],
            shop: {},
            checkout: { lineItems: [] },
            error: null,
            successMessage: null,
            successMessageColor: null,
            isHydratingCheckout: false,
            isSearching: false,
            searchResults: [],
            isMenuOpen: false,
            isShopMenuActive: false,
            hasSeenBanner: false,
            hasSeenNewsletterModal: false,
            backInStockProduct: {
                title: null,
                variantId: null,
                productId: null,
            },
        };
    }

    componentDidMount = () => {
        // Note: We'll skip loading all products for now since that would require additional API calls
        // You can implement product fetching if needed

        this.hydrateCheckout();

        const hasSeenBanner = Boolean(cookie.get(BANNER_KEY));
        this.setState({ hasSeenBanner: hasSeenBanner });

        const hasSeenNewsletter = Boolean(cookie.get(NEWSLETTER_KEY));
        this.setState({ hasSeenNewsletterModal: hasSeenNewsletter });
    };

    hydrateCheckout = async () => {
        const cartId = cookie.get(CART_KEY);

        this.setState({ isHydratingCheckout: true });

        if (cartId) {
            const cart = await fetchCart(cartId);

            if (cart) {
                this.setState({
                    checkout: this.formatCheckout(cart),
                    isHydratingCheckout: false,
                });
            } else {
                // Cart doesn't exist or expired, create new one
                console.log('Cart not found, creating new cart');
                await this.createNewCart();
            }
        } else {
            await this.createNewCart();
        }
    };

    createNewCart = async () => {
        const cart = await createCart();

        if (cart) {
            console.log('New cart created:', cart);
            cookie.set(CART_KEY, cart.id, { expires: 30 });
            this.setState({
                checkout: this.formatCheckout(cart),
                isHydratingCheckout: false,
            });
        } else {
            console.error('Failed to create cart');
            this.setState({ isHydratingCheckout: false });
        }
    };

    // Format the cart to match the expected checkout structure
    formatCheckout = cart => {
        if (!cart) return { lineItems: [] };

        const formattedLineItems = (cart.lineItems || []).map(item => ({
            id: item.id,
            quantity: item.quantity,
            title: item.merchandise?.product?.title || '',
            variant: {
                id: item.merchandise?.id,
                title: item.merchandise?.title || 'Default Title',
                price: {
                    amount: item.merchandise?.price?.amount || '0',
                    currencyCode:
                        item.merchandise?.price?.currencyCode || 'AUD',
                },
                image: item.merchandise?.image
                    ? {
                          src: item.merchandise.image.url,
                      }
                    : null,
                selectedOptions: item.merchandise?.selectedOptions || [
                    { name: 'Title', value: 'Default Title' },
                ],
                product: item.merchandise?.product || {},
            },
            vendor: item.merchandise?.product?.vendor,
        }));

        return {
            id: cart.id,
            lineItems: formattedLineItems,
            subtotalPrice: {
                amount: cart.cost?.subtotalAmount?.amount || '0',
                currencyCode: cart.cost?.subtotalAmount?.currencyCode || 'AUD',
            },
            webUrl: cart.checkoutUrl, // This is the key field we need!
            checkoutUrl: cart.checkoutUrl, // Also store as checkoutUrl for compatibility
            note: cart.note || '',
            totalQuantity: cart.totalQuantity || 0,
        };
    };

    setCartOpen = boolean => {
        this.setState({
            isCartOpen: boolean,
        });
    };

    setSearchOpen = boolean => {
        this.setState({
            isSearchOpen: boolean,
        });
    };

    setAddingToCart = boolean => {
        this.setState({
            isAddingToCart: boolean,
        });
    };

    addVariantToCart = async (variantId, quantity) => {
        this.setState({
            isAddingToCart: true,
        });

        // Encode the variant ID for Storefront API
        const merchandiseId = encode('ProductVariant', variantId, {
            accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_API_TOKEN,
        });

        const cartId = this.state.checkout.id;

        if (!cartId) {
            console.error('No cart ID available');
            this.setState({ isAddingToCart: false });
            return;
        }

        const updatedCart = await addToCart(cartId, merchandiseId, quantity);

        if (updatedCart) {
            this.setState({
                checkout: this.formatCheckout(updatedCart),
                error: null,
                isCartOpen: true,
                isAddingToCart: false,
            });
        } else {
            this.setState({
                error: 'Failed to add item to cart',
                isAddingToCart: false,
            });
        }
    };

    addAllToCart = async (variantIds = []) => {
        this.setState({
            isAddingToCart: true,
        });

        const cartId = this.state.checkout.id;

        if (!cartId) {
            console.error('No cart ID available');
            this.setState({ isAddingToCart: false });
            return;
        }

        // Add items one by one (could be optimized to batch)
        for (const id of variantIds) {
            const merchandiseId = encode('ProductVariant', id, {
                accessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_API_TOKEN,
            });
            await addToCart(cartId, merchandiseId, 1);
        }

        // Fetch updated cart
        const updatedCart = await fetchCart(cartId);

        if (updatedCart) {
            this.setState({
                checkout: this.formatCheckout(updatedCart),
                error: null,
                isCartOpen: true,
                isAddingToCart: false,
            });
        }
    };

    updateQuantityInCart = async (lineItemID, quantity) => {
        const cartId = this.state.checkout.id;

        if (quantity === 0) {
            // Remove item if quantity is 0
            const updatedCart = await removeCartLine(cartId, lineItemID);
            if (updatedCart) {
                this.setState({
                    checkout: this.formatCheckout(updatedCart),
                });
            }
        } else {
            // Update quantity
            const updatedCart = await updateCartLine(
                cartId,
                lineItemID,
                quantity
            );
            if (updatedCart) {
                this.setState({
                    checkout: this.formatCheckout(updatedCart),
                });
            }
        }
    };

    removeLineItemInCart = async lineItemID => {
        const cartId = this.state.checkout.id;
        const updatedCart = await removeCartLine(cartId, lineItemID);

        if (updatedCart) {
            this.setState({
                checkout: this.formatCheckout(updatedCart),
            });
        }
    };

    clearSuccessMessage = () => {
        this.setState({
            successMessageColor: null,
            successMessage: null,
        });
    };

    // Note: fetchProduct would need to be implemented with Storefront API
    fetchProduct = async productId => {
        console.log('fetchProduct not yet implemented for Storefront API');
        return null;
    };

    setIsSearching = value => {
        this.setState({
            isSearching: value,
        });
    };

    setIsMenuOpen = value => {
        this.setState({
            isMenuOpen: value,
        });
    };

    setIsShopMenuActive = value => {
        this.setState({
            isShopMenuActive: value,
        });
    };

    setHasSeenBanner = value => {
        this.setState({
            hasSeenBanner: value,
        });
        cookie.set(BANNER_KEY, true, { expires: 1 });
    };

    setHasSeenNewsletterModal = value => {
        this.setState({
            hasSeenNewsletterModal: value,
        });
        cookie.set(NEWSLETTER_KEY, true, { expires: 1 });
    };

    searchStore = searchTerm => {
        this.setState({
            isSearching: true,
        });
        productSearchIndex
            .search(searchTerm)
            .then(({ hits }) => {
                this.setState({
                    searchResults: hits,
                    isSearching: false,
                });
            })
            .catch(error => {
                console.log(error);
                this.setState({
                    isSearching: false,
                });
            });
    };

    setBackInStockProduct = ({ title, variantId, productId }) => {
        this.setState({
            backInStockProduct: {
                title,
                variantId,
                productId,
            },
        });
    };

    render() {
        const value = {
            // values
            products: this.state.products,
            shop: this.state.shop,
            checkout: this.state.checkout,
            isCartOpen: this.state.isCartOpen,
            isAddingToCart: this.state.isAddingToCart,
            isSearchOpen: this.state.isSearchOpen,
            successMessage: this.state.successMessage,
            successMessageColor: this.state.successMessageColor,
            isSearching: this.state.isSearching,
            searchResults: this.state.searchResults,
            isMenuOpen: this.state.isMenuOpen,
            isShopMenuActive: this.state.isShopMenuActive,
            hasSeenBanner: this.state.hasSeenBanner,
            hasSeenNewsletterModal: this.state.hasSeenNewsletterModal,
            backInStockProduct: this.state.backInStockProduct,

            // methods
            hydrateCheckout: this.hydrateCheckout,
            setCartOpen: this.setCartOpen,
            setAddingToCart: this.setAddingToCart,
            addAllToCart: this.addAllToCart,
            setSearchOpen: this.setSearchOpen,
            addVariantToCart: this.addVariantToCart,
            updateQuantityInCart: this.updateQuantityInCart,
            removeLineItemInCart: this.removeLineItemInCart,
            setSanityProducts: this.setSanityProducts,
            clearSuccessMessage: this.clearSuccessMessage,
            fetchProduct: this.fetchProduct,
            searchStore: this.searchStore,
            setIsSearching: this.setIsSearching,
            setIsMenuOpen: this.setIsMenuOpen,
            setIsShopMenuActive: this.setIsShopMenuActive,
            setHasSeenBanner: this.setHasSeenBanner,
            setHasSeenNewsletterModal: this.setHasSeenNewsletterModal,
            setBackInStockProduct: this.setBackInStockProduct,
        };

        return (
            <ShopContext.Provider value={value}>
                {this.props.children}
            </ShopContext.Provider>
        );
    }
}
